import { useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import HtmlWithLatex from './HtmlWithLatex';
import { MathInputTools } from '../../Student/components/TypeDrawQuestionInput';
import { fillBlankUnderlineStyle } from './fillBlankStyles';

/**
 * Renders a two-level hub-and-spoke diagram where each spoke has:
 *   - An outer corner box showing: large variable letter (A/B/C/D), full equation,
 *     vertical dots going down, then the given outer value
 *   - An inner midway box showing the operator + letter variable blank (e.g., "×A", "−B", "+2C")
 *   - Two arrows: outer → inner → center rectangle
 *
 * Layout (4-spoke):
 *
 *   [A / eq / ... / 0.3]──→[×A]─┐
 *                                ↓
 *   [C / eq / ... / 0.001]─→[+2C]──→[0.027]←──[−B]←──[B / eq / ... / 20% of 10]
 *                                ↑
 *   [D / eq / ... / 0.162]─→[÷D]─┘
 *
 * Modes:
 *   - interactive (inputValues + onInputChange provided): editable inputs inside inner op boxes
 *   - review     (showAnswers=true): correct answer shown; student answer coloured green/red
 *   - paper      (neither):         blank "?" inside inner op box answer slot
 *
 * Props:
 *   blankLabels   string[]          – full equations, e.g. ["0.3 × □ = 0.027", "1.678 - □ = 0.027"]
 *   hubValue      string | undefined – centre rectangle text; auto-detected from labels if omitted
 *   inputValues   string[] | undefined
 *   onInputChange (idx: number, value: string) => void
 *   showAnswers   boolean
 *   correctBlanks any[]             – correct answer per blank
 *   studentAnswers any              – student submitted answers (array or scalar)
 */

// Letter variables assigned by index: A, B, C, D
const VAR_LETTERS = ['A', 'B', 'C', 'D'];

/**
 * Convert plain fraction text like "1/10 of 27" or "20% of 10" into
 * MathJax-renderable LaTeX so fractions display stacked (as on paper).
 * Only converts simple integer fractions, e.g. "1/10" → "$\frac{1}{10}$".
 */
const toDisplayHtml = (val) => {
  if (!val) return val;
  return val.replace(/\b(\d+)\/(\d+)\b/g, (_, n, d) => `$\\frac{${n}}{${d}}$`);
};

// Blank placeholder tokens (same set as DiagramHubSpoke)
const BLANKS = ['□', '[ ]', '[]', '__', '___', '_____', '\\Box', '\\square', '\\Square'];
const stripMathDelimiters = (value) => String(value || '').replace(/\$/g, '').trim();

/**
 * Parses a single blank_label for the outer format.
 *
 * Handles all formats the AI may store:
 *   "0.3 × □ = 0.027"    → { outerValue: "0.3",          innerOp: "× A" }  (ideal □ format)
 *   "0.9 × A = 0.027"    → { outerValue: "0.9",          innerOp: "× A" }  (letter instead of □)
 *   "0.001 + 2□ = 0.027" → { outerValue: "0.001",        innerOp: "+ 2C" }
 *   "1/10 of 27 ÷ □"     → { outerValue: "1/10 of 27",   innerOp: "÷ D" }
 *   "A"                  → { outerValue: "",              innerOp: "A" }    (just letter – no equation)
 */
const parseOuterLabel = (label, idx) => {
  const varLetter = VAR_LETTERS[idx] ?? String.fromCharCode(65 + idx);
  const s = stripMathDelimiters(label);

  // Strip hub-target suffix "= N"
  const withoutTarget = s.replace(/\s*=\s*\S+\s*$/, '').trim();

  // ── Step 1: locate the blank position ────────────────────────────────────
  // First try standard □ placeholders.
  // If none found, try the variable letter itself (A/B/C/D) when it appears
  // after an arithmetic operator – i.e. "0.9 × A" treats "A" as the blank.
  let blankPos = -1;
  for (const ph of BLANKS) {
    const pos = withoutTarget.indexOf(ph);
    if (pos >= 0) { blankPos = pos; break; }
  }

  if (blankPos < 0) {
    // Try the variable letter as blank, but only when it follows an operator
    const escapedLetter = varLetter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const letterAfterOp = new RegExp(`[×÷+\\-−*/]\\s*\\d*\\.?\\d*\\s*${escapedLetter}\\s*$`);
    if (letterAfterOp.test(withoutTarget)) {
      blankPos = withoutTarget.lastIndexOf(varLetter);
    }
  }

  if (blankPos < 0) {
    // No blank or operator pattern found.
    // If the whole string IS just the variable letter, treat it as an unlabelled spoke.
    const isJustLetter = withoutTarget === varLetter;
    return {
      outerValue: isJustLetter ? '' : withoutTarget,
      innerOp: varLetter,
      varLetter,
    };
  }

  // ── Step 2: split before/after the blank ─────────────────────────────────
  const before = withoutTarget.substring(0, blankPos);

  // Find the last arithmetic operator in `before`
  const opChars = ['×', '÷', '+', '−', '-', '*', '/'];
  let opIdx = -1;
  let opChar = '';
  for (let i = before.length - 1; i >= 0; i--) {
    if (opChars.includes(before[i])) {
      const precedingText = before.substring(0, i).trim();
      if (precedingText.length > 0 || i > 0) {
        opIdx = i;
        opChar = before[i];
        break;
      }
    }
  }

  if (opIdx < 0) {
    return { outerValue: before.trim() || withoutTarget, innerOp: varLetter, varLetter };
  }

  const outerValue = before.substring(0, opIdx).trim();
  const coefficient = before.substring(opIdx + 1).trim();
  const displayOp = opChar === '-' ? '−' : opChar === '*' ? '×' : opChar === '/' ? '÷' : opChar;

  const innerOp = coefficient
    ? `${displayOp} ${coefficient}${varLetter}`
    : `${displayOp} ${varLetter}`;

  return { outerValue, innerOp, varLetter };
};

// ── Layout constants (all in viewBox %, container is 100×100 viewBox) ────────
//
// ViewBox: 0 0 120 100  (wider to fit outer boxes at corners)
//
// Outer boxes: ~22% wide, ~18% tall (taller for stacked: letter + eq + dots + value)
// Inner op boxes: ~22% wide, ~13% tall
// Center rect: ~22% wide, ~14% tall

const VW = 120;
const VH = 100;

// Center rectangle dimensions
const CRW = 22; const CRH = 14;
const CCX = VW / 2; const CCY = VH / 2; // 60, 50

// Outer box dimensions
const OBW = 22; const OBH = 13;

// Inner op box dimensions
const IBW = 22; const IBH = 13;

// Spoke geometry per index (0=TL, 1=TR, 2=BL, 3=BR)
const SPOKE_POSITIONS = {
  4: [
    { outer: [12, 12],  inner: [35, 28] },   // TL
    { outer: [108, 12], inner: [85, 28] },   // TR
    { outer: [12, 88],  inner: [35, 72] },   // BL
    { outer: [108, 88], inner: [85, 72] },   // BR
  ],
  3: [
    { outer: [12, 12],  inner: [35, 28] },
    { outer: [108, 12], inner: [85, 28] },
    { outer: [60, 92],  inner: [60, 72] },
  ],
  2: [
    { outer: [8, 50],   inner: [34, 50] },
    { outer: [112, 50], inner: [86, 50] },
  ],
  1: [
    { outer: [60, 8],   inner: [60, 28] },
  ],
};

// Convert viewBox centre + dimensions to CSS % for the overlay
const toPercent = (cx, cy, w, h) => ({
  left:   `${((cx - w / 2) / VW * 100).toFixed(2)}%`,
  top:    `${((cy - h / 2) / VH * 100).toFixed(2)}%`,
  width:  `${(w / VW * 100).toFixed(2)}%`,
  height: `${(h / VH * 100).toFixed(2)}%`,
});

// Arrow: line from edge of box1 toward centre of box2, ending at edge of box2
const computeArrow = (fromCX, fromCY, toCX, toCY, fromHW, fromHH, toHW, toHH) => {
  const dx = toCX - fromCX;
  const dy = toCY - fromCY;
  const mag = Math.sqrt(dx * dx + dy * dy);
  if (mag < 0.01) return null;
  const ux = dx / mag; const uy = dy / mag;

  const tFrom = Math.min(
    ux !== 0 ? Math.abs(fromHW / ux) : Infinity,
    uy !== 0 ? Math.abs(fromHH / uy) : Infinity,
  );
  const x1 = fromCX + ux * tFrom;
  const y1 = fromCY + uy * tFrom;

  const tTo = Math.min(
    ux !== 0 ? Math.abs(toHW / ux) : Infinity,
    uy !== 0 ? Math.abs(toHH / uy) : Infinity,
  );
  const x2 = toCX - ux * tTo;
  const y2 = toCY - uy * tTo;

  return { x1, y1, x2, y2 };
};

const DiagramHubSpokeOuter = ({
  blankLabels = [],
  hubValue: hubValueProp,
  inputValues,
  onInputChange,
  showAnswers = false,
  correctBlanks = [],
  studentAnswers,
  showMathTools = false,
}) => {
  const markerIdRef = useRef(`hso_${Math.random().toString(36).slice(2, 7)}`);
  const markerId = markerIdRef.current;
  const blankInputRefs = useRef({});
  const [activeBlankIdx, setActiveBlankIdx] = useState(0);

  if (!blankLabels || blankLabels.length === 0) return null;

  const items = blankLabels.slice(0, 4);
  const n = items.length;

  // Auto-detect hub value from "= N" suffix on first label
  const hubValue = (() => {
    const raw = hubValueProp || (() => {
      for (const label of items) {
        const m = stripMathDelimiters(label).match(/=\s*(\S+)\s*$/);
        if (m) return m[1];
      }
      return '';
    })();
    return String(raw).replace(/^=\s*/, '').trim();
  })();

  const parsed = items.map((label, i) => parseOuterLabel(label, i));

  const studentArr = (() => {
    if (Array.isArray(studentAnswers)) return studentAnswers;
    if (studentAnswers != null && studentAnswers !== '') return [studentAnswers];
    return [];
  })();

  const getCorrectAnswer = (idx) => {
    const b = correctBlanks[idx];
    if (b == null) return '';
    if (Array.isArray(b)) return String(b[0] ?? '');
    if (typeof b === 'object' && b.text != null) return String(b.text);
    return String(b);
  };

  // Normalize LaTeX fractions for comparison: "\frac{3}{4}" → "3/4"
  const normForMatch = (v) => {
    let s = String(v).replace(/^\$+|\$+$/g, '').trim().toLowerCase();
    s = s.replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '$1/$2');
    return s;
  };

  const isStudentCorrect = (idx) => {
    const s = normForMatch(studentArr[idx] ?? '');
    const c = normForMatch(getCorrectAnswer(idx));
    return s !== '' && c !== '' && s === c;
  };

  const spokeList = (SPOKE_POSITIONS[n] || SPOKE_POSITIONS[4]).slice(0, n);
  const interactive = inputValues != null && typeof onInputChange === 'function';
  const showFillMathTools = showMathTools && interactive;

  const arrows = spokeList.map(({ outer: [ox, oy], inner: [ix, iy] }) => ({
    outerToInner: computeArrow(ox, oy, ix, iy, OBW / 2, OBH / 2, IBW / 2, IBH / 2),
    innerToCenter: computeArrow(ix, iy, CCX, CCY, IBW / 2, IBH / 2, CRW / 2, CRH / 2),
  }));

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 620,
        mx: 'auto',
        my: 1,
        p: 1.5,
        bgcolor: 'white',
      }}
    >
      {showFillMathTools ? (
        <MathInputTools
          getInput={() => blankInputRefs.current[activeBlankIdx] ?? null}
          getValue={() => inputValues[activeBlankIdx] ?? ''}
          onChange={(newVal) => onInputChange(activeBlankIdx, newVal)}
        />
      ) : null}
      {/* Aspect-ratio container matching viewBox 120:100 = 83.3% */}
      <Box sx={{ paddingBottom: '83.33%', position: 'relative' }}>

        {/* ── SVG layer: center rect + spoke arrows ──────────────────────── */}
        <svg
          viewBox={`0 0 ${VW} ${VH}`}
          preserveAspectRatio="xMidYMid meet"
          style={{
            position: 'absolute',
            top: 0, left: 0,
            width: '100%', height: '100%',
            pointerEvents: 'none',
            overflow: 'visible',
          }}
        >
          <defs>
            <marker
              id={markerId}
              markerWidth="5" markerHeight="5"
              refX="4.5" refY="2.5"
              orient="auto"
            >
              <path d="M0,0 L0,5 L5,2.5 z" fill="#4a90d9" />
            </marker>
          </defs>

          {/* Two-hop spoke arrows */}
          {arrows.map((a, i) => (
            <g key={i}>
              {a.outerToInner && (
                <line
                  x1={a.outerToInner.x1} y1={a.outerToInner.y1}
                  x2={a.outerToInner.x2} y2={a.outerToInner.y2}
                  stroke="#4a90d9" strokeWidth="0.7"
                  markerEnd={`url(#${markerId})`}
                />
              )}
              {a.innerToCenter && (
                <line
                  x1={a.innerToCenter.x1} y1={a.innerToCenter.y1}
                  x2={a.innerToCenter.x2} y2={a.innerToCenter.y2}
                  stroke="#4a90d9" strokeWidth="0.7"
                  markerEnd={`url(#${markerId})`}
                />
              )}
            </g>
          ))}

          {/* Centre hub rectangle */}
          <rect
            x={CCX - CRW / 2} y={CCY - CRH / 2}
            width={CRW} height={CRH}
            rx="1.5" ry="1.5"
            fill="#cce5ff"
            stroke="#4a90d9"
            strokeWidth="0.9"
          />

          {/* Hub value label */}
          {hubValue && (
            <text
              x={CCX} y={CCY}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="5.5"
              fontWeight="bold"
              fontFamily="Arial, Helvetica, sans-serif"
              fill="#1565c0"
            >
              {hubValue}
            </text>
          )}
        </svg>

        {/* ── HTML overlay: outer value boxes + inner op boxes ──────────── */}
        {spokeList.map(({ outer: [ox, oy], inner: [ix, iy] }, idx) => {
          const { outerValue, innerOp, varLetter } = parsed[idx];
          // Split operator symbol from the letter for separate sizing
          // Remove only the trailing variable letter (e.g. "× A" → opSymbol="×", "÷ D" → "÷", "+ 2C" → "+ 2")
          const opSymbol = innerOp.replace(new RegExp(`\\s*${varLetter}\\s*$`), '').trim();
          const correctAns = getCorrectAnswer(idx);
          const studentAns = studentArr[idx];
          const hasStudent = studentAns != null && String(studentAns).trim() !== '';
          const correct = isStudentCorrect(idx);

          const outerStyle = toPercent(ox, oy, OBW, OBH);
          const innerStyle = toPercent(ix, iy, IBW, IBH);

          return (
            <Box key={idx}>
              {/* Outer value box (given number, read-only) */}
              <Box
                sx={{
                  position: 'absolute',
                  left: outerStyle.left,
                  top: outerStyle.top,
                  width: outerStyle.width,
                  height: outerStyle.height,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: outerValue ? '1.5px solid #4a90d9' : '1.5px dashed #aaa',
                  borderRadius: '3px',
                  bgcolor: 'white',
                  boxShadow: '0 1px 3px rgba(74,144,217,0.15)',
                  px: 0.5,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    fontWeight: 700,
                    color: outerValue ? '#1a1a1a' : '#bbb',
                    fontSize: '1rem',
                    textAlign: 'center',
                    '& *': { fontSize: 'inherit !important', lineHeight: '1.2 !important' },
                  }}
                >
                  {outerValue
                    ? <HtmlWithLatex html={toDisplayHtml(outerValue)} />
                    : <span style={{ fontStyle: 'italic', fontSize: '0.85rem' }}>—</span>
                  }
                </Box>
              </Box>

              {/* Inner operation box (operator + blank answer slot) */}
              <Box
                sx={{
                  position: 'absolute',
                  left: innerStyle.left,
                  top: innerStyle.top,
                  width: innerStyle.width,
                  height: innerStyle.height,
                  display: 'flex',
                  alignItems: 'stretch',
                  border: '1.5px solid #4a90d9',
                  borderRadius: '3px',
                  overflow: 'hidden',
                  bgcolor: 'white',
                  boxShadow: '0 1px 4px rgba(74,144,217,0.2)',
                }}
              >
                {/* Operator label – blue section: operator + letter both large */}
                <Box
                  sx={{
                    bgcolor: '#cce5ff',
                    flex: '0 0 50%',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '2px',
                    px: 0.4,
                    borderRight: '1px solid #4a90d9',
                    overflow: 'hidden',
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 800,
                      color: '#1565c0',
                      fontSize: '1rem',
                      lineHeight: 1,
                    }}
                  >
                    {opSymbol}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 800,
                      color: '#1565c0',
                      fontSize: '1rem',
                      lineHeight: 1,
                    }}
                  >
                    {varLetter}
                  </Typography>
                </Box>

                {/* Answer slot – white section */}
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 0.5,
                    minWidth: 0,
                    overflow: 'hidden',
                  }}
                >
                  {interactive ? (
                    <input
                      type="text"
                      value={inputValues[idx] ?? ''}
                      onChange={(e) => onInputChange(idx, e.target.value)}
                      onFocus={() => setActiveBlankIdx(idx)}
                      ref={(el) => { blankInputRefs.current[idx] = el; }}
                      style={{
                        width: '100%',
                        border: 'none',
                        outline: 'none',
                        textAlign: 'center',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        background: 'transparent',
                        padding: '6px 2px 2px 2px',
                        ...fillBlankUnderlineStyle,
                        marginTop: '6px',
                        color: '#1a1a1a',
                      }}
                      placeholder="?"
                      aria-label={`Answer for: ${innerOp}`}
                    />
                  ) : showAnswers ? (
                    <Box sx={{
                      textAlign: 'center',
                      lineHeight: 1.2,
                      px: 0.25,
                      width: '100%',
                      fontSize: '0.6rem',
                      '& *': { fontSize: 'inherit !important', lineHeight: '1.2 !important' },
                    }}>
                      {hasStudent && (
                        <Box
                          sx={{
                            fontWeight: 700,
                            color: correct ? 'success.main' : 'error.main',
                            lineHeight: 1.2,
                          }}
                        >
                          <HtmlWithLatex html={String(studentAns)} />
                        </Box>
                      )}
                      <Box
                        sx={{
                          fontWeight: 700,
                          color: 'success.dark',
                          lineHeight: 1.2,
                          borderTop: hasStudent && !correct ? '1px solid #e0e0e0' : 'none',
                          pt: hasStudent && !correct ? '1px' : 0,
                        }}
                      >
                        ✓ <HtmlWithLatex html={correctAns} />
                      </Box>
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '0.65rem', sm: '0.8rem' },
                        color: '#aaa',
                        lineHeight: 1,
                      }}
                    >
                      ?
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default DiagramHubSpokeOuter;
